package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Bolt
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.ThumbDown
import androidx.compose.material.icons.filled.ThumbUp
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ButtonGroupDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3ExpressiveApi
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.chrisbanes.haze.HazeState
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeVoteFailure
import com.bikompanion.domain.ElectricOnlyIssues
import com.bikompanion.domain.NetworkScopeCandidate
import com.bikompanion.ui.mapper.BikeScoreSummaryUiModel
import com.bikompanion.ui.mapper.BikeScoreTone
import com.bikompanion.ui.mapper.BikeScoreVerdict
import com.bikompanion.ui.models.BikeRatingMessage
import com.bikompanion.ui.models.BikeRatingUiState
import com.bikompanion.ui.models.text
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkExpanded
import com.bikompanion.ui.preview.PreviewNetwork
import com.bikompanion.ui.rememberTitleFontFamily
import com.bikompanion.ui.strings.LocalStrings

/**
 * The issues worth flagging, in the order shown. The electric-only ones are filtered out by
 * the caller unless the bike being rated is electric.
 */
private val CommonIssues = listOf(
    BikeIssue.FlatTire,
    BikeIssue.FrontBrakeNotWorking,
    BikeIssue.RearBrakeNotWorking,
    BikeIssue.ChainSlipping,
    BikeIssue.GearsNotShifting,
    BikeIssue.SeatDamagedOrMissing,
    BikeIssue.LightsNotWorking,
    BikeIssue.LockOrBasketDamaged,
    BikeIssue.FrameDamage,
    BikeIssue.ElectricMotorNotWorking,
    BikeIssue.BatteryNotCharging,
    BikeIssue.Other,
)

/** The gap between the sheet's own steps, reused by the nested step columns. */
private val StepSpacing = 12.dp

/**
 * How a revealed step arrives: it grows the sheet from the top edge down, rather than fading
 * into space that was already reserved for it.
 *
 * The default spec behind these is a non-bouncing spring, which is what keeps the steps below a
 * revealed one from being flung past their resting place while the sheet resizes around it.
 */
private val StepEnter: EnterTransition =
    fadeIn() + expandVertically(expandFrom = Alignment.Top)

private val StepExit: ExitTransition =
    fadeOut() + shrinkVertically(shrinkTowards = Alignment.Top)

/**
 * Check or rate a bike, asked one step at a time.
 *
 * Each answer unlocks the next question — network, then number, then the vote and what's wrong
 * with it — because every step is meaningless without the one above it: a number belongs to a
 * network, and a vote belongs to a number. Showing them all up front offered fields that could
 * not be acted on and a vote that quietly refused to submit; revealing them keeps whatever is on
 * screen actually usable.
 */
@Composable
internal fun RateBikeSheet(
    visible: Boolean,
    uiState: BikeRatingUiState,
    hazeState: HazeState,
    onBikeNumberChange: (String) -> Unit,
    onElectricToggle: (Boolean) -> Unit,
    onVoteSelect: (Boolean) -> Unit,
    onIssueToggle: (BikeIssue) -> Unit,
    onNetworkSelect: (BikeNetwork) -> Unit,
    onSubmitClick: () -> Unit,
    onDismiss: () -> Unit,
    docked: Boolean = false,
    dockedWidth: Dp = DockedSheetWidth,
) {
    val enabled = !uiState.isSubmitting
    GlassBottomSheet(
        visible = visible,
        hazeState = hazeState,
        onDismiss = onDismiss,
        docked = docked,
        dockedWidth = dockedWidth,
        verticalArrangement = Arrangement.spacedBy(StepSpacing),
    ) {
        Text(
            text = LocalStrings.current.rateTitle,
            style = MaterialTheme.typography.titleMedium,
            fontFamily = rememberTitleFontFamily(),
            fontSize = 24.sp,
        )
        if (uiState.networkCandidates.isEmpty()) {
            // Nothing resolved from the rider's position yet, so there is no first step to take:
            // say so rather than showing a title above an empty sheet.
            Text(
                text = LocalStrings.current.rateFindingNetworks,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            BikeNetworkPicker(
                candidates = uiState.networkCandidates,
                selectedNetworkId = uiState.selectedNetworkId,
                enabled = enabled,
                onNetworkSelect = onNetworkSelect,
            )
        }
        Step(visible = uiState.canEnterBikeNumber) {
            BikeNumberField(
                bikeNumber = uiState.bikeNumber,
                isCheckingScore = uiState.isCheckingScore,
                enabled = enabled,
                onBikeNumberChange = onBikeNumberChange,
            )
            Step(visible = uiState.canVote) {
                // The lookup result, shown as soon as a number is typed — the point is to know
                // whether a bike is worth taking before you unlock it, not only after voting.
                Step(visible = uiState.scoreSummary != null) {
                    uiState.scoreSummary?.let { BikeScoreSummary(it) }
                }
                ElectricBikeRow(
                    isElectric = uiState.isElectric,
                    enabled = enabled,
                    onElectricToggle = onElectricToggle,
                )
                VoteButtons(
                    selectedVote = uiState.selectedVote,
                    enabled = enabled,
                    onVoteSelect = onVoteSelect,
                )
                Step(visible = uiState.isReportingIssue) {
                    Text(LocalStrings.current.rateWhatsWrong, style = MaterialTheme.typography.titleSmall)
                    IssueChips(
                        issues = CommonIssues.filter {
                            uiState.isElectric || it !in ElectricOnlyIssues
                        },
                        selectedIssues = uiState.selectedIssues,
                        enabled = enabled,
                        onIssueToggle = onIssueToggle,
                    )
                }
            }
        }
        uiState.statusMessage?.let { message ->
            Text(message.text(LocalStrings.current), style = MaterialTheme.typography.bodyMedium)
        }
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.End),
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 8.dp),
        ) {
            Button(onClick = onDismiss) { Text(LocalStrings.current.actionClose) }
            // Always shown so the sheet's actions don't shift around as steps are answered —
            // it just stays disabled until there's a vote to send.
            Button(onClick = onSubmitClick, enabled = uiState.canSubmit) { Text(LocalStrings.current.actionSubmit) }
        }
    }
}

/**
 * One revealed step of the dialog: its [content] is laid out as a column spaced like the sheet
 * itself, so a step holding several rows reads no differently from those rows sitting directly
 * in the sheet.
 */
@Composable
private fun Step(
    visible: Boolean,
    content: @Composable ColumnScope.() -> Unit,
) {
    AnimatedVisibility(visible = visible, enter = StepEnter, exit = StepExit) {
        Column(verticalArrangement = Arrangement.spacedBy(StepSpacing), content = content)
    }
}

@Composable
private fun BikeNumberField(
    bikeNumber: String,
    isCheckingScore: Boolean,
    enabled: Boolean,
    onBikeNumberChange: (String) -> Unit,
) {
    val focusRequester = remember { FocusRequester() }
    // Fires once, when the field first enters composition — the moment the network step above
    // it settles and this one is revealed — so the keyboard is already up by the time the rider
    // would otherwise have had to tap the field themselves.
    LaunchedEffect(Unit) { focusRequester.requestFocus() }
    OutlinedTextField(
        value = bikeNumber,
        onValueChange = onBikeNumberChange,
        label = { Text(LocalStrings.current.rateBikeNumber) },
        singleLine = true,
        enabled = enabled,
        // Bike numbers are printed as plain integers, so there is nothing else worth
        // offering — and the model filters out anything a keyboard still lets through.
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        trailingIcon = {
            if (isCheckingScore) {
                CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
            } else {
                Icon(imageVector = Icons.Default.Search, contentDescription = null)
            }
        },
        modifier = Modifier.fillMaxWidth().focusRequester(focusRequester),
    )
}

@Composable
private fun ElectricBikeRow(
    isElectric: Boolean,
    enabled: Boolean,
    onElectricToggle: (Boolean) -> Unit,
) {
    val wasElectric = remember { mutableStateOf(isElectric) }
    val chargeProgress = remember { Animatable(0f) }
    val chargeAlpha = remember { Animatable(0f) }
    LaunchedEffect(isElectric) {
        if (isElectric && !wasElectric.value) {
            chargeProgress.snapTo(0f)
            chargeAlpha.snapTo(1f)
            chargeProgress.animateTo(1f, animationSpec = tween(durationMillis = 340))
            chargeAlpha.animateTo(0f, animationSpec = tween(durationMillis = 220))
        } else if (!isElectric) {
            chargeProgress.snapTo(0f)
            chargeAlpha.snapTo(0f)
        }
        wasElectric.value = isElectric
    }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
        modifier = Modifier.fillMaxWidth(),
    ) {
        Text(LocalStrings.current.rateElectricBike)
        Box(modifier = Modifier.size(width = 60.dp, height = 40.dp)) {
            if (chargeAlpha.value > 0f) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .scale(1f + chargeProgress.value * 0.1f)
                        .graphicsLayer(alpha = chargeAlpha.value)
                        .border(
                            width = 1.dp,
                            color = ElectricBikeColor.copy(alpha = 0.8f),
                            shape = RoundedCornerShape(20.dp),
                        ),
                )
            }
            Switch(
                checked = isElectric,
                onCheckedChange = onElectricToggle,
                enabled = enabled,
                colors = SwitchDefaults.colors(
                    checkedTrackColor = ElectricBikeColor,
                    checkedThumbColor = MaterialTheme.colorScheme.onPrimary,
                    checkedIconColor = ElectricBikeColor,
                ),
                thumbContent = if (isElectric) {
                    {
                        Icon(
                            imageVector = Icons.Default.Bolt,
                            contentDescription = null,
                            modifier = Modifier.size(16.dp),
                        )
                    }
                } else {
                    null
                },
                modifier = Modifier.align(Alignment.Center),
            )
        }
    }
}

/**
 * Bad on the left, Good on the right — a grouped, connected pair rather than two independent
 * buttons, so the pick reads as one choice with two sides.
 */
@OptIn(ExperimentalMaterial3ExpressiveApi::class)
@Composable
private fun VoteButtons(
    selectedVote: Boolean?,
    enabled: Boolean,
    onVoteSelect: (Boolean) -> Unit,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(ButtonGroupDefaults.ConnectedSpaceBetween),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Button(
            onClick = { onVoteSelect(false) },
            enabled = enabled,
            shapes = ButtonDefaults.shapes(
                shape = ButtonGroupDefaults.connectedLeadingButtonShape,
                pressedShape = ButtonGroupDefaults.connectedLeadingButtonPressShape,
            ),
            colors = if (selectedVote == false) {
                ButtonDefaults.buttonColors(
                    containerColor = BadVoteColor,
                    contentColor = BadVoteContentColor,
                )
            } else {
                ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                    contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            },
            modifier = Modifier.weight(1f),
        ) {
            Icon(imageVector = Icons.Default.ThumbDown, contentDescription = null)
            Text(" " + LocalStrings.current.rateVoteBad)
        }
        Button(
            onClick = { onVoteSelect(true) },
            enabled = enabled,
            shapes = ButtonDefaults.shapes(
                shape = ButtonGroupDefaults.connectedTrailingButtonShape,
                pressedShape = ButtonGroupDefaults.connectedTrailingButtonPressShape,
            ),
            colors = if (selectedVote == true) {
                ButtonDefaults.buttonColors(
                    containerColor = GoodVoteColor,
                    contentColor = GoodVoteContentColor,
                )
            } else {
                ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                    contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            },
            modifier = Modifier.weight(1f),
        ) {
            Icon(imageVector = Icons.Default.ThumbUp, contentDescription = null)
            Text(" " + LocalStrings.current.rateVoteGood)
        }
    }
}

/**
 * Which network the bike belongs to, nearest first.
 *
 * Shown even when one is already selected: the ranking behind it is a guess made from a single
 * published point per network, and a rider standing between two systems is better served by a
 * visible, one-tap correction than by a decision made silently on their behalf.
 */
@Composable
private fun BikeNetworkPicker(
    candidates: List<NetworkScopeCandidate>,
    selectedNetworkId: String?,
    enabled: Boolean,
    onNetworkSelect: (BikeNetwork) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(
            text = if (selectedNetworkId == null) LocalStrings.current.rateWhichNetwork else LocalStrings.current.fieldNetwork,
            style = MaterialTheme.typography.labelLarge,
        )
        Row(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.horizontalScroll(rememberScrollState()),
        ) {
            candidates.forEach { candidate ->
                FilterChip(
                    selected = candidate.network.id == selectedNetworkId,
                    onClick = { onNetworkSelect(candidate.network) },
                    enabled = enabled,
                    label = { Text(candidate.network.name) },
                )
            }
        }
    }
}

private val PreviewCandidates = listOf(
    NetworkScopeCandidate(PreviewNetwork.copy(id = "bicing", name = "Bicing"), distanceKm = 0.4),
    NetworkScopeCandidate(PreviewNetwork.copy(id = "ambici", name = "AMBici"), distanceKm = 2.1),
)

/** A network settled on and a number typed: the point every later step builds on. */
private val PreviewNumberEntered = BikeRatingUiState(
    networkCandidates = PreviewCandidates,
    selectedNetworkId = "bicing",
    bikeNumber = "1234",
)

private val PreviewScored = PreviewNumberEntered.copy(
    scoreSummary = BikeScoreSummaryUiModel(
        bikeNumber = "1234",
        verdict = BikeScoreVerdict.LooksGood,
        tone = BikeScoreTone.Positive,
        percentPositive = 92,
        ratingCount = 25,
    ),
)

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetFindingNetworksPreview() {
    RateBikeSheetPreview(uiState = BikeRatingUiState())
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetPickNetworkPreview() {
    RateBikeSheetPreview(uiState = BikeRatingUiState(networkCandidates = PreviewCandidates))
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetEnterNumberPreview() {
    RateBikeSheetPreview(uiState = PreviewNumberEntered.copy(bikeNumber = ""))
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetCheckingScorePreview() {
    RateBikeSheetPreview(uiState = PreviewNumberEntered.copy(isCheckingScore = true))
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetScoredPreview() {
    RateBikeSheetPreview(uiState = PreviewScored)
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetVoteGoodPreview() {
    RateBikeSheetPreview(uiState = PreviewScored.copy(selectedVote = true))
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetVoteBadPreview() {
    RateBikeSheetPreview(
        uiState = PreviewScored.copy(selectedVote = false, selectedIssues = setOf(BikeIssue.FlatTire)),
    )
}

/** The electric-only issues joining the list. */
@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetVoteBadElectricPreview() {
    RateBikeSheetPreview(
        uiState = PreviewScored.copy(
            isElectric = true,
            selectedVote = false,
            selectedIssues = setOf(BikeIssue.BatteryNotCharging),
        ),
    )
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetSubmittingPreview() {
    RateBikeSheetPreview(uiState = PreviewScored.copy(selectedVote = true, isSubmitting = true))
}

@PreviewLightDarkCompact
@Composable
private fun RateBikeSheetStatusMessagePreview() {
    RateBikeSheetPreview(
        uiState = PreviewScored.copy(
            selectedVote = true,
            statusMessage = BikeRatingMessage.Failed(BikeVoteFailure.Rejected),
        ),
    )
}

@PreviewLightDarkExpanded
@Composable
private fun RateBikeSheetDockedPreview() {
    RateBikeSheetPreview(uiState = PreviewScored.copy(selectedVote = true), docked = true)
}

@Composable
private fun RateBikeSheetPreview(
    uiState: BikeRatingUiState,
    docked: Boolean = false,
) {
    GlassPreviewSurface { hazeState ->
        RateBikeSheet(
            visible = true,
            uiState = uiState,
            hazeState = hazeState,
            onBikeNumberChange = {},
            onElectricToggle = {},
            onVoteSelect = {},
            onIssueToggle = {},
            onNetworkSelect = {},
            onSubmitClick = {},
            onDismiss = {},
            docked = docked,
        )
    }
}
